import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SortComponent } from '../../core/components/sort/sort.component';
import { DatepickerComponent } from '../../core/components/datepicker/datepicker.component';
import { SearchComponent } from '../../core/components/search/search.component';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import { IsNewDirective } from '../../../directives/is-new.directive';
import { StatusComponent } from '../../core/components/status/status.component';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Client, CommandStatus, PageInfo, PermissionsEnum, QuerySequentialTokenDataIn, QuerySequentialTokenDataOut, SequentalTokenDataOut, SortingType, TokenStatus } from '../../../api/api-reference';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../services/auth.service';
import { SignalRService } from '../../../services/signalr.service';
import { ClipboardService } from '../../../services/clipboard.service';
import { SignalRBaseComponent } from '../../../services/signalr-base.component';
import { processDateRange } from '../../../helper/date-utils';
import { DateRangeInput } from '../../core/models/date-range-input';
import { RouterLink } from '@angular/router';
import { SequentialTokenLoadInformationComponent } from './sequential-token-load-information/sequential-token-load-information.component';
import { ConfirmDialogComponent } from '../../core/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-sequential-token-load',
  standalone: true,
  imports: [CommonModule,
    TranslateModule,
    SortComponent,
    DatepickerComponent,
    SearchComponent,
    PaginationComponent,
    IsNewDirective,
    StatusComponent,
    AngularSvgIconModule,
    RouterLink,
    SequentialTokenLoadInformationComponent,
    ConfirmDialogComponent
  ],
  templateUrl: './sequential-token-load.component.html',
  styleUrl: './sequential-token-load.component.scss'
})
export class SequentialTokenLoadComponent extends SignalRBaseComponent {
  public pageSize: number = 10;
  public pageNumber: number = 1;
  public currentPage: number = 1;
  public numberOfCommands: number = 0;
  public lastPageSize: number = 1;
  public selectedId: string | undefined | null;
  public selectedRows: QuerySequentialTokenDataOut[] = [];
  public PermissionsEnum = PermissionsEnum;
  commandStatuses = Object.values(CommandStatus).map(s => s.toUpperCase());

  readonly dayInMs = 24 * 60 * 60 * 1000;
  isDatepickerOpen = false;
  isModalOpen = false;
  isBulkRetryModalOpen = false;

  data: QuerySequentialTokenDataOut[] | undefined = [];
  requestData = new QuerySequentialTokenDataIn({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    search: undefined,
    startDate: undefined,
    endDate: undefined,
    sorting: SortingType.Descending,
    status: undefined,
  });

  toggleModal() {
    this.isModalOpen = !this.isModalOpen;
  }

  isRowSelected(id: string): boolean {
    return this.selectedRows.some(r => r.id === id);
  }

  toggleRowSelection(row: QuerySequentialTokenDataOut) {
    const index = this.selectedRows.findIndex(r => r.id === row.id);
    if (index === -1) {
      this.selectedRows.push(row);
    } else {
      this.selectedRows.splice(index, 1);
    }
  }

  isAllSelected(): boolean {
    return (this.data?.length ?? 0) > 0 && this.data!.every(row => this.isRowSelected(row.id!));
  }

  toggleAllSelected(event: Event) {
    if ((event.target as HTMLInputElement).checked) {
      this.selectedRows = [...(this.data ?? [])];
    } else {
      this.selectedRows = [];
    }
  }

  clearSelection() {
    this.selectedRows = [];
  }

  hasFailedSelected(): boolean {
    return this.selectedRows.some(r => r.commandStatus === CommandStatus.Failed);
  }

  confirmBulkRetry() {
    const failedIds = this.selectedRows
      .filter(r => r.commandStatus === CommandStatus.Failed)
      .map(r => r.id!);

    this.client.onDemandDataBulkRetry(failedIds).subscribe({
      next: () => {
        this.toastr.success('Retry initiated successfully');
        this.isBulkRetryModalOpen = false;
        this.clearSelection();
        this.reloadTable(this.currentPage);
      },
      error: (err) => this.toastr.error(err),
    });
  }

  constructor(
    private client: Client,
    private toastr: ToastrService,
    public authService: AuthService,
    signalRService: SignalRService,
    public clipboardService: ClipboardService
  ) {
    super(signalRService);
    this.reloadTable(1);
  }

  protected override registerHandlers(): void {
    const handler = (message: any) => {
      const item = this.data?.find((item) => item.id === message.commandId);
      if (item) {
        item.commandStatus = message.commandStatus;
        if (message.commandStatus === CommandStatus.Failed) {
          item.failCount = message.failCount
          try {
            const parsed = JSON.parse(message.errorMessage);
            if (parsed.Message) item.errorMessage = parsed.Message;
          } catch {
            if (message.errorMessage) item.errorMessage = message.errorMessage;
          }
        }
      }
    };


    this.registerSignalREvent('bulk-remote-token-execution', handler);
  }

  override async ngOnInit(): Promise<void> {
    await super.ngOnInit();
  }

  ensureDate(dateTime: unknown): Date | null {
    return dateTime instanceof Date ? dateTime : null;
  }

  onSortOption(selectedSort?: SortingType) {
    this.clearSelection();
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.reloadTable();
  }

  onStatusOption(selectedStatus?: string) {
    this.clearSelection();
    if (selectedStatus !== undefined) {
      this.requestData.status =
        selectedStatus === 'ALL'
          ? undefined
          : (selectedStatus as CommandStatus);
    }
    this.reloadTable();
  }

  selectedDate: string | null = '';
  onDateRangeChanged(input: DateRangeInput): void {
    this.clearSelection();
    const result = processDateRange(input);
    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.filterDatesInTable();
  }

  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.reloadTable();
  }

  toggleDatepicker() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  handleRowClick(hesId: string) {
    this.selectedId = hesId;
    this.isModalOpen = true;
  }

  printResponseOfCommand(event: MouseEvent, row: QuerySequentialTokenDataOut) {
    if (row.commandStatus === CommandStatus.Failed) {
      event.stopPropagation();
      this.toastr.error(row.errorMessage);
    }
  }

  isMaxCommandReached(row: QuerySequentialTokenDataOut): boolean {
    return (row.failCount || 0) >= (row.maxRetryCount || 0);
  }

  getCommandTooltipText(row: QuerySequentialTokenDataOut): string {
    return `Retry ${row.failCount || 0} / ${row.maxRetryCount || 0}`;
  }

  retryCommand(event: MouseEvent, row: QuerySequentialTokenDataOut) {
    event.stopPropagation();
    this.client.onDemandSequentialTokenRetry(row.id).subscribe({
      next: () => {
        this.toastr.success('Command retry initiated successfully');
        this.reloadTable(this.currentPage);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  searchChange(searchText: string) {
    this.clearSelection();
    this.requestData.search = searchText;
    this.reloadTable(1);
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;
    this.selectedId = null;

    this.client.sequentialTokenQuery(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfCommands = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfCommands / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  filterDatesInTable() {
    this.client.sequentialTokenQuery(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfCommands = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfCommands / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
}
